import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { order_list } from "../../../../api/calls/order/order_list";
import { setMainModal } from "../../../../reducers/slices/main";
import { scrollToTop } from "../../../../helpers/general";
const FILTER_OPTIONS = [
    { identifier: "all", label: "All" },
    { identifier: "ORDERED", label: "New" },
    { identifier: "PROCESSING", label: "In Progress" },
    { identifier: "FULFILLED", label: "Delivered" },
    { identifier: "CANCELLED", label: "Cancelled" },
];
const defaultPagination = {
    currentPage: 1,
    totalRecords: 0,
    recordsPerPage: 10,
};
export function useOrderListState() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { user } = useSelector((state) => state.userState);
    const [isLoading, setIsLoading] = useState(false);
    const [orders, setOrders] = useState();
    const [filter, setFilter] = useState(FILTER_OPTIONS[0]);
    const [ordersPagination, setOrdersPagination] = useState(defaultPagination);
    // This stores the latest request number so slower older requests cannot replace the newest filter/page results.
    const latestOrderRequestIdRef = useRef(0);
    const hasOrders = useMemo(() => !!orders && orders?.length > 0, [orders]);
    const handleFilterChanged = (target) => {
        setFilter(target);
        setOrdersPagination(defaultPagination);
    };
    const handlePaginationChanged = (page) => {
        setOrdersPagination({
            ...ordersPagination,
            currentPage: page,
        });
    };
    const handleViewOrder = useCallback((order_id) => () => {
        navigate(`/orders/order?orderId=${order_id}`);
    }, []);
    useEffect(() => {
        scrollToTop();
        if (user?.user?._id) {
            fetchOrders();
        }
    }, [user, ordersPagination.currentPage, filter?.identifier]);
    const fetchOrders = async () => {
        const orderRequestId = latestOrderRequestIdRef.current + 1;
        latestOrderRequestIdRef.current = orderRequestId;
        setIsLoading(true);
        let request = {
            size: ordersPagination.recordsPerPage.toString(),
            page: ordersPagination.currentPage.toString(),
        };
        if (filter && filter.identifier !== "all") {
            request.status = filter.identifier;
        }
        order_list(request).then((response) => {
            // Ignore responses from older requests because the user may have changed the filter or page already.
            if (orderRequestId !== latestOrderRequestIdRef.current) {
                return;
            }
            setIsLoading(false);
            if (response.success) {
                setOrders(response.content.order_list);
                setOrdersPagination((currentPagination) => ({
                    ...currentPagination,
                    totalRecords: response.content.count,
                }));
            }
            else {
                dispatch(setMainModal({
                    title: "Could not get order list!",
                    messages: [response.message],
                }));
            }
        });
    };
    return useMemo(() => ({
        orders,
        isLoading,
        handleFilterChanged,
        handleViewOrder,
        hasOrders,
        FILTER_OPTIONS,
        ordersPagination,
        handlePaginationChanged,
    }), [orders, isLoading, handleFilterChanged, handleViewOrder, hasOrders, FILTER_OPTIONS, ordersPagination, handlePaginationChanged]);
}
